import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ColorPickerProps {
  value?: string;
  onChange: (color: string) => void;
  icon: React.ReactNode;
  tooltip: string;
  disabled?: boolean;
  isActive?: boolean;
}

const colors = [
  // Row 1 - Grays
  ['#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff'],
  // Row 2 - Colors
  ['#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff'],
  // Row 3 - Light colors
  ['#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc'],
  // Row 4 - Medium colors
  ['#dd7e6b', '#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#a4c2f4', '#9fc5e8', '#b4a7d6', '#d5a6bd'],
  // Row 5 - Dark colors
  ['#cc4125', '#e06666', '#f6b26b', '#ffd966', '#93c47d', '#76a5af', '#6d9eeb', '#6fa8dc', '#8e7cc3', '#c27ba0'],
];

const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  icon,
  tooltip,
  disabled = false,
  isActive = false,
}) => {
  const [open, setOpen] = React.useState(false);

  const handleColorSelect = (color: string) => {
    onChange(color);
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-8 w-8 p-0', isActive && 'bg-muted')}
          disabled={disabled}
          title={tooltip}
        >
          <div className="relative">
            {icon}
            {value && (
              <div
                className="absolute -bottom-0.5 left-0.5 right-0.5 h-1 rounded-sm"
                style={{ backgroundColor: value }}
              />
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">{tooltip}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={handleClear}
            >
              Clear
            </Button>
          </div>
          <div className="space-y-1">
            {colors.map((row, rowIndex) => (
              <div key={rowIndex} className="flex gap-0.5">
                {row.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      'w-5 h-5 rounded-sm border border-border hover:scale-110 transition-transform',
                      value === color && 'ring-2 ring-primary ring-offset-1'
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => handleColorSelect(color)}
                    title={color}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColorPicker;
